import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'modules/state-mgmt/store';
import { WorkerProfileField } from 'types/profileCustomFields';
import { SkippableTillType } from 'utils/skippableFieldsUtils';
import { PERSONAL_INFO_FIELD_CONFIG } from 'hooks/usePersonalInformationFields';
import { CONTACT_INFO_FIELD_CONFIG } from 'hooks/useContactInformationFields';
import { TRADES_FIELD_CONFIG } from 'modules/views/pages/Trades/hooks/useTradesFields';

type SkippableTillFilter = SkippableTillType | SkippableTillType[];

export interface SkippedFieldInfo {
  fieldName: string;
  skippableTill: SkippableTillType;
}

/**
 * Unified hook to check for skippable fields based on skippableTill type
 * @param skippableTillFilter - Single or array of SkippableTillType to filter by
 * @returns Object containing skipped field info and the earliest screen with missing fields
 */
export const useSkippedFieldsCheck = (skippableTillFilter: SkippableTillFilter) => {
  const { t } = useTranslation();

  const { onboarding, profileFieldConstraintsMap, reduxState } = useAppSelector(state => ({
    onboarding: state.onboarding,
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
    reduxState: state,
  }));

  const result = useMemo(() => {
    const skippedFieldNames: string[] = [];
    const skippedFields: SkippedFieldInfo[] = [];
    let earliestScreen: 'personal' | 'contact' | 'trades' | null = null;

    const allowedTypes = Array.isArray(skippableTillFilter) ? skippableTillFilter : [skippableTillFilter];

    const isFieldEmpty = (value: any): boolean => {
      return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
    };

    const getNestedValue = (obj: any, path: string): any => {
      return path.split('.').reduce((current, key) => current?.[key], obj);
    };

    const shouldCheckField = (field: WorkerProfileField): boolean => {
      const constraints = profileFieldConstraintsMap[field];
      return (
        !!constraints &&
        constraints.isVisible &&
        constraints.isMandatory &&
        constraints.isSkippable &&
        allowedTypes.includes(constraints.skippableTill)
      );
    };

    const checkFields = (fieldConfig: any, screenName: 'personal' | 'contact' | 'trades') => {
      Object.entries(fieldConfig).forEach(([fieldKey, config]: [string, any]) => {
        const field = fieldKey as WorkerProfileField;
        if (!shouldCheckField(field)) return;

        // Check if field has a custom shouldCheck condition
        if (config.shouldCheck) {
          const shouldCheckResult = config.shouldCheck(onboarding, reduxState);
          if (!shouldCheckResult) return;
        }

        // Use reduxField if available, otherwise fall back to formField
        const fieldPath = config.reduxField || config.formField;
        const value = getNestedValue(onboarding, fieldPath);

        if (isFieldEmpty(value)) {
          const fieldName = t(config.displayNameKey);
          const constraints = profileFieldConstraintsMap[field];
          skippedFieldNames.push(fieldName);
          skippedFields.push({
            fieldName,
            skippableTill: constraints.skippableTill,
          });
          if (!earliestScreen) earliestScreen = screenName;
        }
      });
    };

    checkFields(PERSONAL_INFO_FIELD_CONFIG, 'personal');
    checkFields(CONTACT_INFO_FIELD_CONFIG, 'contact');
    checkFields(TRADES_FIELD_CONFIG, 'trades');

    return { skippedFieldNames, skippedFields, earliestScreen };
  }, [onboarding, profileFieldConstraintsMap, t, reduxState, skippableTillFilter]);

  return result;
};

/**
 * Hook to check for Consent Form required fields that were skipped
 * These fields become mandatory once user reaches the consent form
 */
export const useConsentFormSkippedFieldsCheck = () => {
  return useSkippedFieldsCheck(SkippableTillType.ConsentForm);
};

/**
 * Hook to check for Badge-related fields that are optional until badge operations
 * These fields show a gentle reminder but remain optional
 */
export const useBadgeSkippableFieldsCheck = () => {
  return useSkippedFieldsCheck([SkippableTillType.BadgePrinting, SkippableTillType.BadgeActivation]);
};
